<template>
  <div class="customer-edit-profile profile-with-tickets">
    <div class="project-tickets-section">
      <h3>
        {{ constants.TICKETS_CREATED_BY_ME_TEXT }}
      </h3>
      <div class="project-tickets-list">
        <div v-if="createdLoading" class="loading-container">
          <div class="loading-spinner">Loading tickets...</div>
        </div>
        <div v-else>
          <div v-if="createdTickets.length === 0" class="empty-state" style="text-align: center; margin-top: 20px;">
            No ticket is created
          </div>
          <ul v-else>
            <li v-for="issue in createdTickets" :key="issue.id">
              <router-link :to="`/employee/issue-detail/${issue.key}`" id="ticketLink">
                <i>
                  <img :src="issue.fields.issuetype?.iconUrl" alt="" />
                </i>
                <div class="pro-t-l-content">
                  <strong>{{ issue.key }}</strong>
                  <p>{{ issue.fields.summary }}</p>
                </div>
              </router-link>
            </li>
          </ul>
        </div>
      </div>
      <div class="pagination-box" v-if="createdTotalPages > 1">
        <ul>
          <li class="pagination-first" v-if="createdCurrentPage !== 1" @click="changeCreatedPage(createdCurrentPage - 1)">
            <button id="paginationFirstButton"></button>
          </li>
          <li v-for="page in createdVisiblePages" :key="page" @click="changeCreatedPage(page)">
            <a href="#" :class="{ active: createdCurrentPage === page }">{{ page }}</a>
          </li>
          <li class="pagination-last" v-if="createdCurrentPage !== createdTotalPages" @click="changeCreatedPage(createdCurrentPage + 1)">
            <button id="paginationLastButton"></button>
          </li>
        </ul>
      </div>
    </div>
    <div class="edit-profile-pages">
      <h1>
        {{ constants.PROFILE_TEXT }}
      </h1>
      <div class="edit-profile-in">
        <div class="edit-profile-in-l">
          <div class="user-pro-img">
            <img :src="displayImage" width="100%" alt="Profile Image" />
          </div>
          <h3>
            {{ fullName }}
            <span>{{ userProfileData.designation }}</span>
          </h3>
          <ul>
            <li>
              <input
                type="file"
                id="fileUploadInput"
                @change="handleFileUpload"
                accept="image/*"
              /><span>
                {{ constants.UPLOAD_PICTURE_LABEL }}
              </span>
            </li>
            <li>
              <a
                href="javascript:void(0);"
                data-bs-toggle="modal"
                data-bs-target="#deleteProfileimg"
                @click="deleteProfileImage"
              >
                {{ constants.DELETE_PICTURE_LINK }}
              </a>
            </li>
            <li class="active">
              <router-link :to="'/employee/profile'" id="viewProfileLink">
                {{ constants.VIEW_PROFILE_LINK }}
              </router-link>
            </li>
            <li>
              <router-link :to="'/employee/edit-profile'" id="editProfileLink">
                {{ constants.EDIT_PROFILE_LINK }}
              </router-link>
            </li>
          </ul>
        </div>
        <div class="edit-profile-in-r">
          <div class="profile-details">
            <div class="profile-details-row">
              <ul>
                <li>
                  <strong>
                    {{ constants.USERNAME_LABEL }}
                  </strong>
                  <p>{{ userProfileData.userName }}</p>
                </li>
                <li>
                  <strong>
                    {{ constants.EMAIL_ADDRESS_LABEL }}
                  </strong>
                  <p>{{ userProfileData.email }}</p>
                </li>
              </ul>
            </div>
            <div class="profile-details-row">
              <ul>
                <li>
                  <strong>
                    {{ constants.EDUCATION_LABEL }}
                  </strong>
                  <p>{{ userProfileData.education }}</p>
                </li>

                <li>
                  <strong>
                    {{ constants.COMPANY_NAME_LABEL }}
                  </strong>
                  <p>{{ userProfileData.comapanyName }}</p>
                </li>
              </ul>
            </div>
            <div class="profile-details-row">
              <ul>
                <li>
                  <strong>
                    {{ constants.COMPANY_WEBSITE_LABEL }}
                  </strong>
                  <p>{{ userProfileData.websiteUrl }}</p>
                </li>
                <li>
                  <strong>
                    {{ constants.CONTACT_NO_LABEL }}
                  </strong>
                  <p>{{ userProfileData.contactNumber }}</p>
                </li>
              </ul>
            </div>

            <div class="profile-details-row">
              <strong>
                {{ constants.ABOUT_ME_LABEL }}
              </strong>
              <p>
                {{ userProfileData.aboutUs }}
              </p>
            </div>
            <div class="profile-details-row  employee-project-details">
              <ul>
                <li>
                  <strong>
                    {{ constants.PROJECT_LABEL }}
                  </strong>
                  <p
                    v-for="project in userProfileData.projects"
                    :key="project.id"
                  >
                    {{ project.name }}
                  </p>
                </li>
                <li>
                  <strong>
                    {{ constants.ROLE_LABEL }}
                  </strong>
                  <p
                    v-for="project in userProfileData.projects"
                    :key="project.id"
                  >
                    {{ project.role }}
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="clear"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import constants from "@/utils/en-US-Employee.json";
import axios from "axios";
import { API_BASE_URL, API_IMAGES_BASE_URL } from "@/config/api.config";
import Swal from "sweetalert2";
import eventBus from "@/utils/eventBus";

interface UserProject {
  id: string;
  name: string;
  role: string;
}

interface AvatarUrls {
  [key: string]: string;
}

interface IssueFields {
  summary: string;
  issuetype: { iconUrl: string };
}

interface Issue {
  id: string;
  key: string;
  fields: IssueFields;
}

export default defineComponent({
  name: "EmployeeProfile",
  data() {
    return {
      constants,
      userProfileData: {
        userName: "",
        email: "",
        firstName: "",
        lastName: "",
        designation: "",
        education: "",
        comapanyName: "",
        websiteUrl: "",
        contactNumber: "",
        aboutUs: "",
        avatarUrls: {} as AvatarUrls,
        profileImage: "",
        projects: [] as UserProject[],
      },
      profileImage: "",
      selectedFile: null as File | null,
      // Tickets created by me
      createdTickets: [] as Issue[],
      createdLoading: false,
      createdCurrentPage: 1,
      createdTotalPages: 0,
      createdLimit: 20,
      createdStart: 0,
    };
  },
  computed: {
    fullName(): string {
      const firstName = this.userProfileData.firstName || "";
      const lastName = this.userProfileData.lastName || "";
      return `${firstName} ${lastName}`.trim() || "User";
    },
    displayImage(): string {
      if (this.userProfileData.profileImage) {
        return `${API_IMAGES_BASE_URL}${this.userProfileData.profileImage}`;
      }

      if (
        this.userProfileData.avatarUrls &&
        Object.keys(this.userProfileData.avatarUrls).length > 0
      ) {
        const avatarUrls = this.userProfileData.avatarUrls;
        if (avatarUrls["48x48"]) {
          return avatarUrls["48x48"];
        } else if (avatarUrls["32x32"]) {
          return avatarUrls["32x32"];
        } else {
          return Object.values(avatarUrls)[0] as string;
        }
      }

      return "@/assets/lems-v1/images/default-user-img.jpg";
    },
    createdVisiblePages(): number[] {
      const maxVisiblePages = 5;
      const half = Math.floor(maxVisiblePages / 2);
      let start = Math.max(this.createdCurrentPage - half, 1);
      let end = start + maxVisiblePages - 1;
      if (end > this.createdTotalPages) {
        end = this.createdTotalPages;
        start = Math.max(end - maxVisiblePages + 1, 1);
      }
      return Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);
    },
  },
  async mounted() {
    await this.fetchUserProfile();

    // Fetch tickets created by me
    await this.getCreatedTickets();

    // Listen for delete confirmation
    eventBus.on(
      "profile-image-delete-confirmed",
      this.performDeleteProfileImage
    );
  },

  beforeUnmount() {
    // Clean up event listener
    eventBus.off(
      "profile-image-delete-confirmed",
      this.performDeleteProfileImage
    );
  },
  methods: {
    async fetchUserProfile() {
      try {
        const username = localStorage.getItem("jira_user_username");
        if (!username) {
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/jira/profile/${username}/`
        );

        this.userProfileData = response.data.user;
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load profile data. Please try again.",
        });
      }
    },

    async getCreatedTickets() {
      const username = localStorage.getItem("jira_user_username");
      if (!username) return;

      this.createdLoading = true;
      try {
        const params = new URLSearchParams({
          start: this.createdStart.toString(),
          limit: this.createdLimit.toString(),
          filter: 'reported_by_me',
          date_filter: 'all',
        });
        const response = await axios.get(`${API_BASE_URL}/jira/issues/${username}/?${params.toString()}`);
        this.createdTickets = response.data.issues || [];
        this.createdTotalPages = response.data.total_pages || 0;
      } catch (error) {
        console.error('Error fetching created tickets:', error);
        this.createdTickets = [];
        this.createdTotalPages = 0;
      } finally {
        this.createdLoading = false;
      }
    },

    changeCreatedPage(page: number) {
      if (page > 0 && page <= this.createdTotalPages) {
        this.createdCurrentPage = page;
        this.createdStart = (page - 1) * this.createdLimit;
        this.getCreatedTickets();
      }
    },

    async handleFileUpload(event: any) {
      const target = event.target as HTMLInputElement;
      if (target && target.files && target.files.length > 0) {
        this.selectedFile = target.files[0];

        try {
          const username = localStorage.getItem("jira_user_username");
          if (!username) {
            return;
          }

          const formData = new FormData();
          formData.append("profileImage", this.selectedFile);

          const response = await axios.put(
            `${API_BASE_URL}/jira/profile/${username}/`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          await this.fetchUserProfile();
          // Emit event to update header
          eventBus.emit("profile-updated");
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Profile image updated successfully",
          });
        } catch (error) {
          console.error("Error uploading profile image:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to upload profile image. Please try again.",
          });
        } finally {
          this.selectedFile = null;
        }
      }
    },

    async deleteProfileImage() {
      // Show the modal using Bootstrap's data attributes
      const modal = document.getElementById("deleteProfileimg");
      if (modal) {
        // Add backdrop and show modal
        const backdrop = document.createElement("div");
        backdrop.className = "modal-backdrop fade show";
        document.body.appendChild(backdrop);

        modal.classList.add("show");
        modal.style.display = "block";
        modal.setAttribute("aria-hidden", "false");
        document.body.classList.add("modal-open");
      }
    },

    async performDeleteProfileImage() {
      try {
        const username = localStorage.getItem("jira_user_username");
        if (!username) {
          return;
        }

        const response = await axios.put(
          `${API_BASE_URL}/jira/profile/${username}/`,
          { profileImage: null }
        );
        await this.fetchUserProfile();
        window.location.reload();
        // Emit event to update header
        eventBus.emit("profile-updated");
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Profile image deleted successfully",
        });
      } catch (error) {
        console.error("Error deleting profile image:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete profile image. Please try again.",
        });
      }
    },
  },
});
</script>
